// Единая кнопка удаления с подтверждением. Раньше каждый экран собирал свой
// Popconfirm: где-то без описания последствий, где-то без loading, а у половины
// сущностей кнопки не было вовсе при живом роуте удаления.
import type { ReactElement } from 'react';
import { Button, Popconfirm, Tooltip, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

interface DeleteEntityButtonProps {
  /** «Удалить сделку?» — вопрос в подтверждении. */
  title: string;
  /** Что произойдёт со связанными записями. Показывать, если связи есть. */
  description?: string;
  /**
   * Промис — предпочтительный контракт (`mutateAsync`): отказ сервера тогда
   * показывается тостом, а не проглатывается закрывшимся Popconfirm. Экраны со
   * своим показом ошибки могут передавать синхронный `mutate`.
   */
  onConfirm: () => void | Promise<unknown>;
  loading?: boolean;
  disabled?: boolean;
  /** Иконка вместо текста — для колонок «действия» в гридах. */
  iconOnly?: boolean;
  size?: 'small' | 'middle';
  /** Подпись на кнопке и в aria-label: «Удалить задачу». */
  label?: string;
  /** Подпись подтверждающей кнопки. Для встреч — «Отменить»: там не удаление, а отмена. */
  okText?: string;
}

export function DeleteEntityButton({
  title,
  description,
  onConfirm,
  loading = false,
  disabled = false,
  iconOnly = false,
  size = 'small',
  label,
  okText,
}: DeleteEntityButtonProps): ReactElement {
  const { t } = useTranslation();
  const visibleLabel = label ?? t('crm.shared.delete');
  const visibleOkText = okText ?? t('crm.shared.delete');
  const handleConfirm = async (): Promise<void> => {
    try {
      await onConfirm();
    } catch (err) {
      const detail = err instanceof Error && err.message.length > 0 ? `: ${err.message}` : '';
      void message.error(t('crm.shared.delete_error', { detail }));
    }
  };

  const button = iconOnly ? (
    <Button
      size={size}
      type="text"
      danger
      disabled={disabled}
      loading={loading}
      aria-label={visibleLabel}
      icon={<DeleteOutlined />}
    />
  ) : (
    <Button size={size} danger disabled={disabled} aria-label={visibleLabel}>
      {visibleLabel}
    </Button>
  );

  return (
    <Popconfirm
      title={title}
      {...(description ? { description } : {})}
      okText={visibleOkText}
      cancelText={t('crm.common.cancel')}
      okButtonProps={{ danger: true, loading }}
      onConfirm={handleConfirm}
    >
      {iconOnly ? <Tooltip title={visibleLabel}>{button}</Tooltip> : button}
    </Popconfirm>
  );
}
